<template>
  <div class="section">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/admin">Application Logs</router-link></li>
        <li class="is-active"><router-link to="/admin/user-audit-logs" aria-current="true">Admin Logs</router-link></li>
      </ul>
    </nav>
    <div class="container">
      <h1 class="title">Admin Logs</h1>

      <div class="column is-12 tabs is-toggle is-small mt-4">
        <ul>
          <li :class="{ 'is-active': activeTab === 'all' }" @click="setTab('all')">
            <a>All</a>
          </li>
          <li :class="{ 'is-active': activeTab === 'admin' }" @click="setTab('admin')">
            <a>Admin</a>
          </li>
          <li :class="{ 'is-active': activeTab === 'non-admin' }" @click="setTab('non-admin')">
            <a>Non-Admin</a>
          </li>
        </ul>
      </div>

      
      <!-- Filters -->
      <div class="field is-grouped mt-3">
        <div class="control">
          <input v-model="filters.start_date" class="input is-small" type="date" placeholder="Start Date">
        </div>
        <div class="control">
          <input v-model="filters.end_date" class="input is-small" type="date" placeholder="End Date">
        </div>
        <div class="control">
          <select v-model="filters.action" class="input is-small">
            <option value="">All Actions</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
          </select>
        </div>
        <div class="control">
          <input v-model="filters.request_user" class="input is-small" type="text" placeholder="Request User">
        </div>
        <div class="control">
          <input v-model="filters.target_user" class="input is-small" type="text" placeholder="Target User">
        </div>
        <div class="control">
          <button class="button is-primary is-small" @click="applyFilters">Apply Filters</button>
        </div>
        <div class="control">
          <button class="button is-warning is-small" @click="resetFilters">Reset Filters</button>
        </div>
        <div class="control">
          <button class="button is-success is-small" @click="downloadCSV">Download CSV</button>
        </div>
        <div class="control">
          <button class="button is-link is-small" @click="downloadAccessSnapshotCSV">Historical Access</button>
        </div>
      </div>

      <!-- Logs Table -->
      <table class="table is-fullwidth is-small mt-5">
        <thead>
          <tr>
            <th class="is-size-7">Request User</th>
            <th class="is-size-7">Request User Type</th>
            <th class="is-size-7">Updated User</th>
            <th class="is-size-7">Action</th>
            <th class="is-size-7">Field</th>
            <th class="is-size-7">Old Value</th>
            <th class="is-size-7">New Value</th>
            <th class="is-size-7">Timestamp</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logs" :key="log.id">
            <td class="is-size-7">{{ log.request_user_email }}</td>
            <td class="is-size-7">{{ log.is_admin ? 'Admin' : 'Non-Admin' }}</td>
            <td class="is-size-7">{{ log.target_user_email }}</td>
            <td class="is-size-7">{{ log.action }}</td>
            <td class="is-size-7">{{ log.field_name || 'N/A' }}</td>
            <td class="is-size-7">{{ log.old_value || 'N/A' }}</td>
            <td class="is-size-7">{{ log.new_value || 'N/A' }}</td>
            <td class="is-size-7">{{ new Date(log.timestamp).toLocaleString() }}</td>
          </tr>
        </tbody>
      </table>
      <button v-if="nextPage" class="button is-primary is-small" @click="fetchLogs(nextPage)">Load More</button>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import { toast } from "bulma-toast";

export default {
  name: 'AuditLogs',
  data() {
    return {
      logs: [],
      nextPage: null,
      filters: {
        start_date: '',
        end_date: '',
        action: '',
        request_user: '',
        target_user: '',
        is_admin: '',
      },
      activeTab: 'all',  // Initialize the activeTab with a default value
    };
  },
  computed: {
    filteredLogs() {
      if (this.activeTab === 'admin') return this.logs.filter(log => log.is_admin === true);
      if (this.activeTab === 'non-admin') return this.logs.filter(log => log.is_admin === false);
      return this.logs;
    }
  },
  methods: {
    async fetchLogs(url = null, append = true) {
      const apiUrl = url || '/api/v1/user-audit-logs/';
      try {
        const response = await axios.get(apiUrl, {
          headers: { 'Authorization': `Token ${localStorage.getItem('token')}` },
          params: this.filters, // Apply filters
        });
        if (append) {
          this.logs = this.logs.concat(response.data.results);
        } else {
          this.logs = response.data.results;
        }
        this.nextPage = response.data.next;
      } catch (error) {
        console.error('Error fetching logs:', error);
        toast({
          message: "Failed to load logs. Please try again.",
          type: "is-danger",
          duration: 5000,
          position: "top-right",
        });
      }
    },
    setTab(tab) {
      this.activeTab = tab;
      // Update the is_admin filter based on the selected tab
      if (tab === 'admin') {
        this.filters.is_admin = 'True';
      } else if (tab === 'non-admin') {
        this.filters.is_admin = 'False';
      } else {
        this.filters.is_admin = '';  // Reset for 'All' tab
      }
      this.fetchLogs(null, false); // Fetch logs with the updated filters
    },
    applyFilters() {
      this.fetchLogs(null, false); // Fetch logs with updated filters
    },
    resetFilters() {
      this.filters = {
        start_date: '',
        end_date: '',
        action: '',
        request_user: '',
        target_user: '',
        is_admin: '',
      };
      this.fetchLogs(null, false); // Refresh logs
    },
    async downloadCSV() {
      try {
        const response = await axios.get('/api/v1/user-audit-logs/csv/', {
          headers: { 'Authorization': `Token ${localStorage.getItem('token')}` },
          params: this.filters,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'audit_logs.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast({
          message: "CSV downloaded successfully.",
          type: "is-success",
          duration: 3000,
          position: "top-right",
        });
      } catch (error) {
        console.error('Error downloading CSV:', error);
        toast({
          message: "Failed to download CSV. Please try again.",
          type: "is-danger",
          duration: 5000,
          position: "top-right",
        });
      }
    },
    async downloadAccessSnapshotCSV() {
      try {
        const response = await axios.get('/api/v1/user-access-snapshots/export/', {
          headers: { 'Authorization': `Token ${localStorage.getItem('token')}` },
          params: {
            start_date: this.filters.start_date,
            end_date: this.filters.end_date,
            request_user: this.filters.request_user,
            target_user: this.filters.target_user
          },
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'user_access_snapshots.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast({
          message: "Historical Access CSV downloaded.",
          type: "is-success",
          duration: 3000,
          position: "top-right",
        });
      } catch (error) {
        console.error('Error downloading access snapshot CSV:', error);
        toast({
          message: "Failed to download historical access CSV.",
          type: "is-danger",
          duration: 5000,
          position: "top-right",
        });
      }
    },
  },
  async created() {
    await this.fetchLogs(); // Fetch logs when component is created
  },
};
</script>


<style scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}
</style>
